import React, { useState, useEffect } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import './EditAllocationAmountModal.css';

const EditAllocationAmountModal = ({ isOpen, onClose, allocation, onSuccess }) => {
    const [baseAmount, setBaseAmount] = useState('');
    const [hasDiscount, setHasDiscount] = useState(false);
    const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
    const [discountValue, setDiscountValue] = useState('');
    const [advanceAmount, setAdvanceAmount] = useState('');
    const [processing, setProcessing] = useState(false);

    // Initialize form with current allocation data
    useEffect(() => {
        if (allocation && isOpen) {
            setBaseAmount(allocation.total_amount || '');
            setAdvanceAmount(allocation.advance_amount || '0');
            const hasExistingDiscount = allocation.discount_type && allocation.discount_type !== 'none';
            setHasDiscount(hasExistingDiscount);
            if (hasExistingDiscount) {
                setDiscountType(allocation.discount_type === 'fixed' ? 'amount' : allocation.discount_type);
                setDiscountValue(allocation.discount_value || '');
            } else {
                setDiscountType('amount');
                setDiscountValue('');
            }
        }
    }, [allocation, isOpen]);

    // Calculate payable amount based on inputs
    const calculatePayableAmount = (amount, hasDisc, type, value) => {
        const baseAmt = parseFloat(amount) || 0;
        
        if (!hasDisc || !value) {
            return baseAmt;
        }
        
        const discVal = parseFloat(value) || 0;

        if (type === 'percentage') {
            return baseAmt - (baseAmt * discVal / 100);
        } else if (type === 'amount') {
            return baseAmt - discVal;
        }
        return baseAmt;
    };

    // Calculate old and new payable amounts
    const oldPayableAmount = allocation ? parseFloat(allocation.payable_amount) : 0;
    const newPayableAmount = calculatePayableAmount(baseAmount, hasDiscount, discountType, discountValue);

    // Check if values have changed
    const hasChanges = () => {
        if (!allocation) return false;
        
        const oldBase = parseFloat(allocation.total_amount);
        const newBase = parseFloat(baseAmount);
        const oldAdvance = parseFloat(allocation.advance_amount) || 0;
        const newAdvance = parseFloat(advanceAmount) || 0;
        const oldHasDiscount = allocation.discount_type && allocation.discount_type !== 'none';
        const oldType = allocation.discount_type === 'fixed' ? 'amount' : (allocation.discount_type || 'amount');
        const oldValue = parseFloat(allocation.discount_value) || 0;
        const newValue = parseFloat(discountValue) || 0;

        return oldBase !== newBase || 
               oldAdvance !== newAdvance ||
               oldHasDiscount !== hasDiscount ||
               (hasDiscount && (oldType !== discountType || oldValue !== newValue));
    };

    const handleSubmit = async () => {
        // Validate inputs
        if (!baseAmount || parseFloat(baseAmount) <= 0) {
            toastError('Please enter a valid base amount');
            return;
        }

        if (hasDiscount) {
            if (!discountValue || parseFloat(discountValue) <= 0) {
                toastError('Please enter a valid discount value');
                return;
            }

            if (discountType === 'percentage' && parseFloat(discountValue) > 100) {
                toastError('Discount percentage cannot exceed 100%');
                return;
            }

            if (discountType === 'amount' && parseFloat(discountValue) >= parseFloat(baseAmount)) {
                toastError('Discount amount cannot be greater than or equal to base amount');
                return;
            }
        }

        // Validate advance amount
        const newAdvance = parseFloat(advanceAmount) || 0;
        const paidAdvance = parseFloat(allocation.advance_paid) || 0;
        const originalAdvance = parseFloat(allocation.advance_amount) || 0;

        // If fully paid, cannot edit advance amount
        if (paidAdvance > 0 && paidAdvance === originalAdvance && newAdvance !== originalAdvance) {
            toastError('Cannot edit advance amount - already fully paid');
            return;
        }

        // If partially paid, new advance cannot be less than paid amount
        if (paidAdvance > 0 && newAdvance < paidAdvance) {
            toastError(`Advance amount cannot be less than already paid amount (₹${paidAdvance.toFixed(2)})`);
            return;
        }

        if (!hasChanges()) {
            toastError('No changes detected');
            return;
        }

        // Save directly without confirmation
        setProcessing(true);

        try {
            const payload = {
                total_amount: parseFloat(baseAmount),
                discount_type: !hasDiscount ? null : (discountType === 'amount' ? 'fixed' : discountType),
                discount_value: !hasDiscount ? null : (parseFloat(discountValue) || null),
                advance_amount: parseFloat(advanceAmount) || 0
            };

            await apiPost(`/allocation/payment/${allocation.allocation_id}/update`, payload);

            toastSuccess('Payment details updated successfully');
            
            // Call onSuccess to refresh parent component
            if (onSuccess) {
                onSuccess();
            }

            // Close modal
            onClose();
        } catch (error) {
            console.error('Error updating payment details:', error);
            
            // Handle different error response formats
            let errorMessage = 'Failed to update payment details';
            
            if (error.response?.data) {
                const data = error.response.data;
                
                // Check if it's a FastAPI validation error (array of objects)
                if (Array.isArray(data.detail)) {
                    errorMessage = data.detail.map(err => err.msg || err.message).join(', ');
                } else if (typeof data.detail === 'string') {
                    errorMessage = data.detail;
                } else if (data.message) {
                    errorMessage = data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toastError(errorMessage);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        if (!processing) {
            onClose();
        }
    };

    if (!allocation) return null;

    return (
        <>
            {processing && <RTSpinner fullScreen={true} />}
            
            <RTModal isOpen={isOpen} onClose={handleClose} title="Edit Allocation Amount">
                <div className="edit-allocation-modal-wrapper">
                    {/* Form Section */}
                    <div className="edit-allocation-form-section" style={{display: 'flex', gap: '20px'}}>
                        {/* Left Side - Base Amount and Discount */}
                        <div style={{flex: 1, maxWidth: '50%'}}>
                            <div className="edit-allocation-form-group">
                                <label className="edit-allocation-form-label">Base Amount (₹) *</label>
                                <input
                                    type="number"
                                    className="edit-allocation-form-control"
                                    value={baseAmount}
                                    onChange={(e) => setBaseAmount(e.target.value)}
                                    min="0"
                                    step="0.01"
                                    disabled={processing}
                                />
                            </div>

                            <div className="edit-allocation-form-group edit-allocation-checkbox-group">
                                <label className="edit-allocation-checkbox-label align-left">
                                    <input
                                        type="checkbox"
                                        checked={hasDiscount}
                                        onChange={(e) => {
                                            setHasDiscount(e.target.checked);
                                            if (!e.target.checked) {
                                                setDiscountValue('');
                                                setDiscountType('amount');
                                            }
                                        }}
                                        disabled={processing}
                                    />
                                    <span style={{marginLeft: '8px'}}>Apply Discount</span>
                                </label>
                            </div>

                            {hasDiscount && (
                                <>
                                    <div className="edit-allocation-discount-type-section" style={{marginBottom: '20px'}}>
                                        <label className="edit-allocation-radio-label">
                                            <input
                                                type="radio"
                                                value="amount"
                                                checked={discountType === 'amount'}
                                                onChange={(e) => {
                                                    setDiscountType(e.target.value);
                                                    setDiscountValue('');
                                                }}
                                                disabled={processing}
                                            />
                                            <span style={{marginLeft: '8px', display: 'inline'}}>Amount (₹)</span>
                                        </label>
                                        <label className="edit-allocation-radio-label">
                                            <input
                                                type="radio"
                                                value="percentage"
                                                checked={discountType === 'percentage'}
                                                onChange={(e) => {
                                                    setDiscountType(e.target.value);
                                                    setDiscountValue('');
                                                }}
                                                disabled={processing}
                                            />
                                            <span style={{marginLeft: '8px', display: 'inline'}}>Percentage (%)</span>
                                        </label>
                                    </div>

                                    <div className="edit-allocation-form-group">
                                        <label className="edit-allocation-form-label">
                                            Discount {discountType === 'percentage' ? '(%)' : '(₹)'} *
                                        </label>
                                        <input
                                            type="number"
                                            className="edit-allocation-form-control"
                                            value={discountValue}
                                            onChange={(e) => setDiscountValue(e.target.value)}
                                            min="0"
                                            step="0.01"
                                            max={discountType === 'percentage' ? '100' : undefined}
                                            disabled={processing}
                                            placeholder={discountType === 'percentage' ? '0-100' : '0'}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Right Side - Advance Amount */}
                        <div style={{flex: 1}}>
                            <div className="edit-allocation-form-group">
                                <label className="edit-allocation-form-label">
                                    Advance Amount (₹) *
                                    {parseFloat(allocation.advance_paid) > 0 && parseFloat(allocation.advance_paid) === parseFloat(allocation.advance_amount) && (
                                        <span style={{marginLeft: '8px', fontSize: '0.9em', color: '#666'}}>
                                            (Fully Paid - Cannot Edit)
                                        </span>
                                    )}
                                    {parseFloat(allocation.advance_paid) > 0 && parseFloat(allocation.advance_paid) < parseFloat(allocation.advance_amount) && (
                                        <span style={{marginLeft: '8px', fontSize: '0.9em', color: '#666'}}>
                                            (Min: ₹{parseFloat(allocation.advance_paid).toFixed(2)} - Already Paid)
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="number"
                                    className="edit-allocation-form-control"
                                    value={advanceAmount}
                                    onChange={(e) => setAdvanceAmount(e.target.value)}
                                    min="0"
                                    step="0.01"
                                    disabled={processing || (parseFloat(allocation.advance_paid) > 0 && parseFloat(allocation.advance_paid) === parseFloat(allocation.advance_amount))}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Summary Comparison Section */}
                    <div className="edit-allocation-summary-section">
                        <h4 className="edit-allocation-summary-title">Payment Summary</h4>
                        <table className="edit-allocation-summary-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Old Value</th>
                                    <th>New Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Base Amount</td>
                                    <td>₹{parseFloat(allocation.total_amount).toFixed(2)}</td>
                                    <td className={parseFloat(baseAmount) !== parseFloat(allocation.total_amount) ? 'text-changed' : ''}>
                                        ₹{parseFloat(baseAmount || 0).toFixed(2)}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Has Discount</td>
                                    <td>{(allocation.discount_type && allocation.discount_type !== 'none') ? 'Yes' : 'No'}</td>
                                    <td className={(allocation.discount_type && allocation.discount_type !== 'none') !== hasDiscount ? 'text-changed' : ''}>
                                        {hasDiscount ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                {(hasDiscount || (allocation.discount_type && allocation.discount_type !== 'none')) && (
                                    <>
                                        <tr>
                                            <td>Discount Type</td>
                                            <td>{allocation.discount_type === 'fixed' ? 'Amount' : allocation.discount_type === 'percentage' ? 'Percentage' : '-'}</td>
                                            <td className={(allocation.discount_type === 'fixed' ? 'amount' : allocation.discount_type) !== discountType ? 'text-changed' : ''}>
                                                {hasDiscount ? (discountType === 'amount' ? 'Amount' : 'Percentage') : '-'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Discount Value</td>
                                            <td>
                                                {allocation.discount_value 
                                                    ? (allocation.discount_type === 'percentage' 
                                                        ? `${parseFloat(allocation.discount_value).toFixed(2)}%`
                                                        : `₹${parseFloat(allocation.discount_value).toFixed(2)}`)
                                                    : '-'
                                                }
                                            </td>
                                            <td className={parseFloat(discountValue || 0) !== parseFloat(allocation.discount_value || 0) ? 'text-changed' : ''}>
                                                {hasDiscount && discountValue
                                                    ? (discountType === 'percentage'
                                                        ? `${parseFloat(discountValue).toFixed(2)}%`
                                                        : `₹${parseFloat(discountValue).toFixed(2)}`)
                                                    : '-'
                                                }
                                            </td>
                                        </tr>
                                    </>
                                )}
                                <tr className="summary-total">
                                    <td><strong>Final Payable Amount</strong></td>
                                    <td><strong>₹{oldPayableAmount.toFixed(2)}</strong></td>
                                    <td className={newPayableAmount !== oldPayableAmount ? 'text-changed' : ''}>
                                        <strong>₹{newPayableAmount.toFixed(2)}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Advance Amount Summary */}
                        <div style={{marginTop: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #dee2e6'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr style={{backgroundColor: '#153C7F', color: 'white'}}>
                                        <th style={{padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Field</th>
                                        <th style={{padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Old Value</th>
                                        <th style={{padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>New Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="summary-total">
                                        <td style={{padding: '15px 12px'}}><strong>Advance Amount</strong></td>
                                        <td style={{padding: '15px 12px'}}><strong>₹{parseFloat(allocation.advance_amount || 0).toFixed(2)}</strong></td>
                                        <td style={{padding: '15px 12px'}} className={parseFloat(advanceAmount || 0) !== parseFloat(allocation.advance_amount || 0) ? 'text-changed' : ''}>
                                            <strong>₹{parseFloat(advanceAmount || 0).toFixed(2)}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="edit-allocation-modal-actions">
                        <RTButton
                            variant="secondary"
                            onClick={handleClose}
                            disabled={processing}
                            className="edit-allocation-btn-secondary"
                        >
                            Cancel
                        </RTButton>
                        <RTButton
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={processing || !hasChanges()}
                            className="edit-allocation-btn-primary"
                        >
                            Save Changes
                        </RTButton>
                    </div>
                </div>
            </RTModal>
        </>
    );
};

export default EditAllocationAmountModal;
